/**
 * El diálogo que cierra un caso o le agrega una nota.
 *
 * Existe porque confirmar o descartar **exige motivo** (`_NEEDS_RESOLUTION`
 * en `services/api/routers/cases.py`), y el motivo no es burocracia: el
 * reparto entre `false_positive` y `counterfeit_confirmed` es el único dato
 * con el que se recalibra el umbral de una regla. Sin él, la discusión sobre
 * si el motor "avisa de más" no tiene números.
 *
 * Se opera entero con el teclado: abre con el foco en el primer control,
 * `Enter` envía, `Esc` cancela. Es la mitad del criterio "se puede resolver un
 * caso sin tocar el mouse".
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useI18n } from '@/i18n/useI18n';
import { Button } from '@/ui/Button';
import { RESOLUTIONS, resolutionLabelKey, statusLabelKey, type AlertStatus } from './severity';

export type CaseDialogMode =
  | { kind: 'status'; status: AlertStatus }
  | { kind: 'note' };

export interface CaseDialogSubmit {
  resolution: string | null;
  note: string | null;
}

export function CaseDialog({
  mode,
  isPending,
  onCancel,
  onSubmit,
}: {
  mode: CaseDialogMode;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: (input: CaseDialogSubmit) => void;
}): ReactNode {
  const { t } = useI18n();
  const [resolution, setResolution] = useState<string>(RESOLUTIONS[0]);
  const [note, setNote] = useState('');
  const firstControl = useRef<HTMLSelectElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    firstControl.current?.focus();
  }, []);

  const statusKey = mode.kind === 'status' ? statusLabelKey(mode.status) : null;
  const title =
    mode.kind === 'note'
      ? t('alerts.dialog.noteTitle')
      : t('alerts.dialog.statusTitle', {
          status: statusKey === null ? mode.status : t(statusKey),
        });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full max-w-md rounded-(--radius-card) border border-border bg-surface-raised p-5 shadow-lg"
      >
        <h2 className="text-base font-semibold text-ink">{title}</h2>

        <form
          className="mt-4 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            const trimmed = note.trim();
            onSubmit({
              resolution: mode.kind === 'status' ? resolution : null,
              note: trimmed.length > 0 ? trimmed : null,
            });
          }}
        >
          {mode.kind === 'status' && (
            <label className="block">
              <span className="text-xs tracking-wide text-ink-subtle uppercase">
                {t('alerts.dialog.resolution')}
              </span>
              <select
                ref={(element) => {
                  if (element !== null) firstControl.current = element;
                }}
                className="mt-1 h-10 w-full rounded border border-border-strong bg-surface px-2 text-sm text-ink"
                value={resolution}
                onChange={(event) => { setResolution(event.target.value); }}
                onKeyDown={(event) => {
                  // El navegador ya envía el formulario con Enter desde un
                  // select, pero no todos y no siempre. Explícito: cerrar un
                  // caso sin mouse es criterio de aceptación, no un extra.
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    event.currentTarget.form?.requestSubmit();
                  }
                }}
              >
                {RESOLUTIONS.map((value) => {
                  const key = resolutionLabelKey(value);
                  return (
                    <option key={value} value={value}>
                      {key === null ? value : t(key)}
                    </option>
                  );
                })}
              </select>
            </label>
          )}

          <label className="block">
            <span className="text-xs tracking-wide text-ink-subtle uppercase">
              {mode.kind === 'note' ? t('alerts.dialog.note') : t('alerts.dialog.noteOptional')}
            </span>
            <textarea
              ref={(element) => {
                if (element !== null && mode.kind === 'note') firstControl.current = element;
              }}
              className="mt-1 h-28 w-full rounded border border-border-strong bg-surface px-2 py-2 text-sm text-ink"
              value={note}
              required={mode.kind === 'note'}
              onChange={(event) => { setNote(event.target.value); }}
              onKeyDown={(event) => {
                // En un textarea, Enter es un salto de línea. El envío rápido
                // sin mouse es Ctrl/Cmd+Enter, como en cualquier editor.
                if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                  event.currentTarget.form?.requestSubmit();
                }
              }}
            />
            <span className="mt-1 block text-xs text-ink-subtle">
              {t('alerts.dialog.submitHint')}
            </span>
          </label>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onCancel} disabled={isPending}>
              {t('alerts.dialog.cancel')}
            </Button>
            <Button
              type="submit"
              variant={mode.kind === 'status' && mode.status === 'confirmed' ? 'critical' : 'primary'}
              disabled={isPending}
            >
              {isPending ? t('alerts.dialog.saving') : t('alerts.dialog.submit')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** La chuleta de atajos. Se abre con `?` y se cierra con `Esc`. */
export function ShortcutHelp({
  rows,
  onClose,
}: {
  rows: readonly { keys: string; label: string }[];
  onClose: () => void;
}): ReactNode {
  const { t } = useI18n();
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('alerts.shortcuts.title')}
        className="w-full max-w-sm rounded-(--radius-card) border border-border bg-surface-raised p-5"
      >
        <h2 className="text-base font-semibold text-ink">{t('alerts.shortcuts.title')}</h2>
        <dl className="mt-3 space-y-1">
          {rows.map((row) => (
            <div key={row.keys} className="flex items-baseline justify-between gap-4">
              <dt className="font-mono text-sm text-ink">{row.keys}</dt>
              <dd className="text-sm text-ink-muted">{row.label}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-4 flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            {t('alerts.dialog.cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
}
